import { toPng, toSvg } from 'html-to-image'
import { slimSvgDataUrl } from './slimSvg'
import type { Relation, Table } from '@/types'
import { calcPath } from '@/utils/paths'
import { getTableHeight, getTableWidth } from '@/utils/geometry'
import { getRelationshipLabel } from '@/utils/relationships'

const EXPORT_PADDING = 80
const GRID_MINOR = 24
const GRID_MAJOR = GRID_MINOR * 5
const MAX_CANVAS_DIMENSION = 16384
const MAX_CANVAS_PIXELS = 268_000_000

export type ExportDiagramParams = {
  element: HTMLElement
  filename: string
  width?: number
  height?: number
  backgroundColor?: string
  pixelRatio?: number
  filter?: (node: HTMLElement) => boolean
}

export type ExportDiagramSceneParams = ExportDiagramParams & {
  tables: Table[]
  relationships: Relation[]
  includeGrid?: boolean
  theme?: 'light' | 'dark'
}

type DiagramBounds = {
  minX: number
  minY: number
  maxX: number
  maxY: number
  width: number
  height: number
}

type ExportScene = {
  element: HTMLElement
  width: number
  height: number
  cleanup: () => void
}

function isExportNode(node: Node): node is HTMLElement {
  return node instanceof HTMLElement
}

function expandBounds(
  bounds: DiagramBounds | null,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): DiagramBounds {
  if (!bounds) {
    return {
      minX,
      minY,
      maxX,
      maxY,
      width: Math.max(1, maxX - minX),
      height: Math.max(1, maxY - minY),
    }
  }

  const nextMinX = Math.min(bounds.minX, minX)
  const nextMinY = Math.min(bounds.minY, minY)
  const nextMaxX = Math.max(bounds.maxX, maxX)
  const nextMaxY = Math.max(bounds.maxY, maxY)

  return {
    minX: nextMinX,
    minY: nextMinY,
    maxX: nextMaxX,
    maxY: nextMaxY,
    width: Math.max(1, nextMaxX - nextMinX),
    height: Math.max(1, nextMaxY - nextMinY),
  }
}

export function sanitizeFilename(value: string): string {
  const trimmed = String(value || 'morpherd').trim() || 'morpherd'
  const ascii = trimmed
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')

  return ascii || 'morpherd'
}

export function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename
  link.click()
}

function waitForNextFrame(): Promise<void> {
  return new Promise<void>((resolve) => {
    let resolved = false
    const done = () => { if (!resolved) { resolved = true; resolve() } }
    // Fallback for headless/background tabs where rAF may be throttled
    setTimeout(done, 50)
    requestAnimationFrame(() => requestAnimationFrame(done))
  })
}

export function getDiagramBounds(
  tables: Table[],
  relationships: Relation[],
): DiagramBounds | null {
  let bounds: DiagramBounds | null = null
  const tablesById = new Map(tables.map((table) => [table.id, table]))

  for (const table of tables) {
    bounds = expandBounds(
      bounds,
      table.x,
      table.y,
      table.x + getTableWidth(table),
      table.y + getTableHeight(table),
    )
  }

  for (const relationship of relationships) {
    const fromTable = tablesById.get(relationship.from)
    const toTable = tablesById.get(relationship.to)
    if (!fromTable || !toTable) continue

    const path = calcPath(relationship, fromTable, toTable)
    const label = getRelationshipLabel(relationship)
    const labelWidth = label.length * 6.5 + 18
    const labelHeight = 18
    const labelX = path.midPoint.x - labelWidth / 2
    const labelY = path.midPoint.y - 22

    for (const point of path.points) {
      bounds = expandBounds(
        bounds,
        point.x - 24,
        point.y - 24,
        point.x + 24,
        point.y + 24,
      )
    }

    bounds = expandBounds(
      bounds,
      labelX - 8,
      labelY - 8,
      labelX + labelWidth + 8,
      labelY + labelHeight + 8,
    )
  }

  return bounds
}

function buildGridStyle(
  width: number,
  height: number,
  minX: number,
  minY: number,
  padding: number,
  theme: 'light' | 'dark',
): Partial<CSSStyleDeclaration> {
  const minorColor = theme === 'dark' ? 'rgba(120,145,170,.035)' : 'rgba(120,140,165,.05)'
  const majorColor = theme === 'dark' ? 'rgba(130,155,180,.07)' : 'rgba(120,140,165,.09)'
  const minorOffsetX = ((padding - minX) % GRID_MINOR + GRID_MINOR) % GRID_MINOR
  const minorOffsetY = ((padding - minY) % GRID_MINOR + GRID_MINOR) % GRID_MINOR
  const majorOffsetX = ((padding - minX) % GRID_MAJOR + GRID_MAJOR) % GRID_MAJOR
  const majorOffsetY = ((padding - minY) % GRID_MAJOR + GRID_MAJOR) % GRID_MAJOR

  return {
    position: 'absolute',
    inset: '0',
    width: `${width}px`,
    height: `${height}px`,
    pointerEvents: 'none',
    backgroundImage: [
      `linear-gradient(${minorColor} 1px, transparent 1px)`,
      `linear-gradient(90deg, ${minorColor} 1px, transparent 1px)`,
      `linear-gradient(${majorColor} 1px, transparent 1px)`,
      `linear-gradient(90deg, ${majorColor} 1px, transparent 1px)`,
    ].join(', '),
    backgroundSize: [
      `${GRID_MINOR}px ${GRID_MINOR}px`,
      `${GRID_MINOR}px ${GRID_MINOR}px`,
      `${GRID_MAJOR}px ${GRID_MAJOR}px`,
      `${GRID_MAJOR}px ${GRID_MAJOR}px`,
    ].join(', '),
    backgroundPosition: [
      `${minorOffsetX}px ${minorOffsetY}px`,
      `${minorOffsetX}px ${minorOffsetY}px`,
      `${majorOffsetX}px ${majorOffsetY}px`,
      `${majorOffsetX}px ${majorOffsetY}px`,
    ].join(', '),
  }
}

function createExportScene({
  element,
  tables,
  relationships,
  width,
  height,
  backgroundColor,
  includeGrid = false,
  theme = 'light',
}: ExportDiagramSceneParams): ExportScene {
  const board = element.querySelector<HTMLElement>('#board')
  if (!board) {
    throw new Error('EXPORT_BOARD_NOT_FOUND')
  }

  const bounds = getDiagramBounds(tables, relationships)
  if (!bounds) {
    throw new Error('EXPORT_EMPTY_DIAGRAM')
  }

  const outputWidth = Math.ceil(width ?? bounds.width + EXPORT_PADDING * 2)
  const outputHeight = Math.ceil(height ?? bounds.height + EXPORT_PADDING * 2)
  const translateX = Math.round(EXPORT_PADDING - bounds.minX)
  const translateY = Math.round(EXPORT_PADDING - bounds.minY)

  // Wrapper: positioned via an off-screen clip container so html-to-image can
  // correctly compute child styles. Using `position: fixed; left: -100000px`
  // causes browsers to skip layout for very off-screen fixed elements,
  // resulting in blank exports. Instead we use a 0×0 overflow:hidden container
  // at the viewport origin so the wrapper's layout is computed normally.
  const clipContainer = document.createElement('div')
  clipContainer.style.position = 'fixed'
  clipContainer.style.top = '0'
  clipContainer.style.left = '0'
  clipContainer.style.width = '0'
  clipContainer.style.height = '0'
  clipContainer.style.overflow = 'hidden'
  clipContainer.style.pointerEvents = 'none'
  clipContainer.style.zIndex = '-1'

  const wrapper = document.createElement('div')
  wrapper.setAttribute('data-export-scene', 'true')
  wrapper.style.position = 'absolute'
  wrapper.style.left = '0'
  wrapper.style.top = '0'
  wrapper.style.width = `${outputWidth}px`
  wrapper.style.height = `${outputHeight}px`
  wrapper.style.overflow = 'hidden'
  wrapper.style.background = backgroundColor ?? '#ffffff'
  wrapper.style.pointerEvents = 'none'
  wrapper.style.isolation = 'isolate'

  if (includeGrid) {
    const grid = document.createElement('div')
    Object.assign(
      grid.style,
      buildGridStyle(outputWidth, outputHeight, bounds.minX, bounds.minY, EXPORT_PADDING, theme),
    )
    wrapper.appendChild(grid)
  }

  const boardClone = board.cloneNode(true)
  if (!(boardClone instanceof HTMLElement)) {
    throw new Error('EXPORT_CLONE_FAILED')
  }

  boardClone.style.transform = `translate(${translateX}px, ${translateY}px)`
  boardClone.style.transformOrigin = '0 0'
  boardClone.style.position = 'absolute'
  boardClone.style.inset = '0'
  boardClone.style.width = `${outputWidth}px`
  boardClone.style.height = `${outputHeight}px`
  boardClone.style.overflow = 'visible'

  boardClone.querySelectorAll('#svgh, .eth-btns').forEach((node) => node.remove())

  wrapper.appendChild(boardClone)
  clipContainer.appendChild(wrapper)
  document.body.appendChild(clipContainer)

  return {
    element: wrapper,
    width: outputWidth,
    height: outputHeight,
    cleanup: () => {
      clipContainer.remove()
    },
  }
}

function validatePngDimensions(width: number, height: number, pixelRatio: number) {
  const targetWidth = width * pixelRatio
  const targetHeight = height * pixelRatio
  if (
    targetWidth > MAX_CANVAS_DIMENSION ||
    targetHeight > MAX_CANVAS_DIMENSION ||
    targetWidth * targetHeight > MAX_CANVAS_PIXELS
  ) {
    throw new Error('EXPORT_CANVAS_TOO_LARGE')
  }
}

async function exportWithRenderer(
  renderer: typeof toPng | typeof toSvg,
  params: ExportDiagramSceneParams,
  transform: (dataUrl: string) => string = (dataUrl) => dataUrl,
): Promise<void> {
  const scene = createExportScene(params)

  try {
    await waitForNextFrame()

    const filter = (node: HTMLElement) => {
      if (node.dataset.exportScene === 'true') return true
      if (node.id === 'svgh') return false
      if (node.classList?.contains('eth-btns')) return false
      if (node.dataset.exportExclude === 'true') return false
      return params.filter ? params.filter(node) : true
    }

    const dataUrl = await renderer(scene.element, {
      cacheBust: true,
      skipFonts: true,
      pixelRatio: params.pixelRatio,
      backgroundColor: params.backgroundColor,
      width: scene.width,
      height: scene.height,
      filter: (node) => !isExportNode(node) || filter(node),
      style: {
        width: `${scene.width}px`,
        height: `${scene.height}px`,
      },
    })

    downloadDataUrl(transform(dataUrl), params.filename)
  } finally {
    scene.cleanup()
  }
}

export async function exportDiagramAsPng(params: ExportDiagramSceneParams): Promise<void> {
  const bounds = getDiagramBounds(params.tables, params.relationships)
  if (!bounds) {
    throw new Error('EXPORT_EMPTY_DIAGRAM')
  }

  const width = Math.ceil(params.width ?? bounds.width + EXPORT_PADDING * 2)
  const height = Math.ceil(params.height ?? bounds.height + EXPORT_PADDING * 2)
  const pixelRatio = params.pixelRatio ?? 2
  validatePngDimensions(width, height, pixelRatio)

  await exportWithRenderer(toPng, {
    ...params,
    width,
    height,
    pixelRatio,
  })
}

export async function exportDiagramAsSvg(params: ExportDiagramSceneParams): Promise<void> {
  const bounds = getDiagramBounds(params.tables, params.relationships)
  if (!bounds) {
    throw new Error('EXPORT_EMPTY_DIAGRAM')
  }

  await exportWithRenderer(
    toSvg,
    {
      ...params,
      width: Math.ceil(params.width ?? bounds.width + EXPORT_PADDING * 2),
      height: Math.ceil(params.height ?? bounds.height + EXPORT_PADDING * 2),
      pixelRatio: 1,
    },
    slimSvgDataUrl,
  )
}
